import type { Expression, Map as MapboxMap } from 'mapbox-gl';
import { ensureCoordalSvgIcons, removeCoordalSvgIcons, clearCoordalSvgIconCache } from './coordalIconLoader';
import {
  catalogEntries,
  catalogLayerPath,
  runtimeCatalogIconIds,
  type CatalogEntry,
  type IconLayer,
  type IconTheme,
  tierSpec,
} from './iconCatalog';
import { mergePoiLabelsFilter, TRANSPORT_POI_MAKI_SLUGS } from './transportPoiExclusions';
import { buildLandmarkIconImage } from './landmarkIconImage';

const POI_LABELS_LAYER = 'ghana-poi-labels';
const LANDMARK_LABELS_LAYER = 'ghana-landmark-labels';
const STRUCTURES_LABEL_LAYER = 'structures-label';

const TRAFFIC_SIGNALS_LAYER = 'traffic-signals-symbol';
const AIRPORT_LABEL_LAYER = 'airport-label';
const ROAD_EXIT_SHIELD_LAYER = 'road-exit-shield';
const TREES_SYMBOL_LAYER = 'ghana-trees-tile';

/** Custom symbol layers on Mapbox Standard dark — icons need emissive paint. */
const DARK_EMISSIVE_SYMBOL_LAYERS = [
  POI_LABELS_LAYER,
  LANDMARK_LABELS_LAYER,
  STRUCTURES_LABEL_LAYER,
  TRAFFIC_SIGNALS_LAYER,
  'structure-furniture-symbol',
  AIRPORT_LABEL_LAYER,
  ROAD_EXIT_SHIELD_LAYER,
  TREES_SYMBOL_LAYER,
] as const;

function applyDarkSymbolPaint(map: MapboxMap) {
  for (const layerId of DARK_EMISSIVE_SYMBOL_LAYERS) {
    if (!map.getLayer(layerId)) continue;
    map.setPaintProperty(layerId, 'icon-emissive-strength', 1);
    if (map.getLayoutProperty(layerId, 'text-field') != null) {
      map.setPaintProperty(layerId, 'text-emissive-strength', 1);
    }
  }
}

function isTransportPoiEntry(entry: CatalogEntry): boolean {
  const slug = entry.id.replace('coordal-poi-', '');
  return TRANSPORT_POI_MAKI_SLUGS.has(slug);
}

function poiIconRef(slug: string): string {
  if (slug === 'harbor') return 'coordal-poi-water';
  if (TRANSPORT_POI_MAKI_SLUGS.has(slug)) return 'coordal-poi-marker';
  return `coordal-poi-${slug}`;
}

/** Strip ["image", match, ...spriteFallbacks] — sprite fallback interferes with runtime icons. */
function unwrapImageExpression(expr: unknown): unknown {
  if (Array.isArray(expr) && expr[0] === 'image' && expr.length >= 2) {
    return expr[1];
  }
  return expr;
}

/** Rewrite any remaining Maki sprite names to Coordal POI ids. */
function replaceMakiWithCoordalPoi(expr: unknown): Expression {
  if (typeof expr === 'string') {
    if (!expr.endsWith('-15')) return expr as unknown as Expression;
    return poiIconRef(expr.slice(0, -3)) as unknown as Expression;
  }
  if (Array.isArray(expr)) {
    return expr.map(replaceMakiWithCoordalPoi) as Expression;
  }
  return expr as Expression;
}

function normalizePoiIconImage(iconImage: unknown): Expression {
  return replaceMakiWithCoordalPoi(unwrapImageExpression(iconImage)) as Expression;
}

function isPoiFilterPatched(filter: unknown): boolean {
  const serialized = JSON.stringify(filter);
  return (
    serialized.includes('bus_station') &&
    serialized.includes('charging_station') &&
    serialized.includes('public_transport')
  );
}

async function loadLayerIcons(
  map: MapboxMap,
  layer: IconLayer,
  entries: CatalogEntry[],
  theme: IconTheme = 'standard',
) {
  const runtime = entries.filter((e) => !e.sprite);
  if (runtime.length === 0) return;
  await ensureCoordalSvgIcons(
    map,
    catalogLayerPath(layer, theme),
    runtime.map((e) => ({ id: e.id, file: e.file })),
  );
}

export async function applyCoordalCatalogIcons(
  map: MapboxMap,
  theme: IconTheme = 'standard',
): Promise<void> {
  const poiForMap = catalogEntries('poi').filter((e) => !isTransportPoiEntry(e));
  const landmarkAll = catalogEntries('landmark');
  const structureAll = catalogEntries('structure');

  await loadLayerIcons(map, 'poi', poiForMap, theme);
  await loadLayerIcons(map, 'landmark', landmarkAll, 'standard');
  await loadLayerIcons(map, 'structure', structureAll, 'standard');
  if (map.getLayer(POI_LABELS_LAYER)) {
    const currentFilter = map.getFilter(POI_LABELS_LAYER);
    if (!isPoiFilterPatched(currentFilter)) {
      map.setFilter(POI_LABELS_LAYER, mergePoiLabelsFilter(currentFilter));
    }

    const current = map.getLayoutProperty(POI_LABELS_LAYER, 'icon-image');
    if (current) {
      map.setLayoutProperty(POI_LABELS_LAYER, 'icon-image', normalizePoiIconImage(current));
    }

    const poiSize = tierSpec('standard').mapIconSize;
    map.setLayoutProperty(POI_LABELS_LAYER, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      poiSize[0],
      poiSize[1],
      poiSize[2],
      poiSize[3],
      poiSize[4],
      poiSize[5],
    ]);
  }

  if (map.getLayer(LANDMARK_LABELS_LAYER) && landmarkAll.length > 0) {
    const landmarkImage = buildLandmarkIconImage(landmarkAll);
    map.setLayoutProperty(LANDMARK_LABELS_LAYER, 'icon-image', landmarkImage);

    const size = tierSpec('landmark').mapIconSize;
    map.setLayoutProperty(LANDMARK_LABELS_LAYER, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      size[0],
      size[1],
      size[2],
      size[3],
      size[4],
      size[5],
    ]);
  }

  if (map.getLayer(STRUCTURES_LABEL_LAYER)) {
    const poiSize = tierSpec('standard').mapIconSize;
    map.setLayoutProperty(STRUCTURES_LABEL_LAYER, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      poiSize[0],
      poiSize[1],
      poiSize[2],
      poiSize[3],
      poiSize[4],
      poiSize[5],
    ]);
  }

  if (theme === 'dark') {
    applyDarkSymbolPaint(map);
  }
}

export function resetCoordalCatalogIcons(map?: MapboxMap) {
  clearCoordalSvgIconCache();
  if (!map) return;
  // Traffic icons live on the style sprite sheet (catalog sprite: true) — never removeImage them.
  removeCoordalSvgIcons(map, runtimeCatalogIconIds());
}
